import type { NavigationGuardReturn } from 'vue-router'

import { getAuthorizer } from '@/auth/useAuthorizer'
import { useAuthStore } from '@/auth/useAuthStore'
import { logError } from '@/logger'
import { getRedirectToLogin } from '@/router/guards/common/getRedirectToLogin'
import { useFeatureFlags } from '@/utils/useFeatureFlags'

import { checkPermissionsV1 } from './checkPermissionsV1'
import { checkPermissionsV2 } from './checkPermissionsV2'
import { ROUTER_ENFORCEMENT_FLAG } from './rbacFlags'
import { type HermesGuardFn } from './types'

const formatResult = (
  result: NavigationGuardReturn
): 'allowed' | 'redirected' => (result === undefined ? 'allowed' : 'redirected')

export const checkPermissions: HermesGuardFn = async (to) => {
  const authStore = useAuthStore()
  const featureFlags = useFeatureFlags()

  if (to.meta.isPublic) return
  if (!authStore.user) return getRedirectToLogin(to)

  const authorizer = getAuthorizer()
  if (!authorizer) {
    // No authorizer = no V2 comparison possible, just use V1
    return checkPermissionsV1(to, authStore)
  }

  // Run both checks and compare. The flag decides which is authoritative —
  // V1 stays the default until we have enough confidence in the V2 authZ
  // predicates declared on each route.
  //
  // The `featureFlags.isEnabled` call below also auto-tags the current RUM
  // view with `@feature_flags.hermes_rbac_routerEnforcement` (see
  // `apps/hermes/src/plugins/featureFlags.ts`). That gives us a per-view
  // "guard ran" signal in Datadog without an explicit heartbeat, so silence
  // on the discrepancy error means no divergence rather than broken wiring.
  const v1Result = checkPermissionsV1(to, authStore)
  const v2Result = checkPermissionsV2(to, authStore)

  const v1Allowed = v1Result === undefined
  const v2Allowed = v2Result === undefined
  const flagEnabled = featureFlags.isEnabled(ROUTER_ENFORCEMENT_FLAG)

  // Disagreement is the only signal worth emitting per-navigation. We log the
  // authoritative path and flag state alongside the raw results so Datadog
  // queries can group divergent routes by `authoritative` / `flagEnabled`.
  if (v1Allowed !== v2Allowed) {
    logError(new Error('Permission check discrepancy between V1 and V2'), {
      authoritative: flagEnabled ? 'v2' : 'v1',
      context: 'checkPermissions',
      flagEnabled,
      meta: to.meta,
      route: to.path,
      routeName: to.name,
      v1Result: formatResult(v1Result),
      v2Result: formatResult(v2Result),
    })
  }

  return flagEnabled ? v2Result : v1Result
}
